import React, { Component } from 'react'
import { connect } from 'react-redux'
import { isDirty as isFormDirty } from 'redux-form'
import classnames from 'classnames'

import {
  selectRefiAppCoverageUiState,
  selectRefiAppVehicleUiState,
  selectRefiAppProfileUiState,
  selectCurrentRefiUser,
  selectCoverageStepState,
  selectCreditSessionFinished,
} from '../../../reducers'

import {
  toggleCoverageUiState,
  coverageDetailsPreviousStep,
} from '../refiActions'
import { refreshCoverageProducts } from '../credit/creditApplicationChoice'

import MembershipSection from './MembershipSection'
import PaymentOptions from './paymentOptions'
import CoverageProtectionSection from './CoverageProtectionSection'
import LoadingSection from '../../../components/loadingSection'
import { selectProductPackageLoading } from '../../../reducers/productPackages'

import { User } from '../../../core/types'

const hasFormChanges = (state, formName: string) => {
  return isFormDirty(formName)(state) && !!state?.form?.[formName]?.anyTouched
}

const submitFormByName = (
  formName: string,
  autosaveTarget: 'vehicle' | 'profile' | 'coverage'
) => {
  const form = document.querySelector(
    `form[name="${formName}"]`
  ) as HTMLFormElement | null

  if (!form) return

  form.setAttribute('data-autosave', 'true')
  form.setAttribute('data-autosave-target', autosaveTarget)

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit()
    return
  }

  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
}

export type Callback<T = void> = (...args: any) => T

export interface Props {
  uiStatus: boolean
  user: User
  toggleCoverageUiState: Callback
  coverageDetailsPreviousStep: Callback
  refreshCoverageProducts: Callback<Promise<any>>
  coverageDetailStep: string
  creditSectionFinished: boolean
  isCreatingPackage: boolean
  vehicleUiStatus: boolean
  profileUiStatus: boolean
  vehicleFormChanged: boolean
  profileFormChanged: boolean
}

export class CoverageDetailsSections extends Component<Props> {
  static displayName = 'CoverageDetailsSections'

  render() {
    const {
      uiStatus,
      user,
      toggleCoverageUiState,
      coverageDetailsPreviousStep,
      refreshCoverageProducts,
      coverageDetailStep,
      creditSectionFinished,
      isCreatingPackage,
      vehicleUiStatus,
      profileUiStatus,
      vehicleFormChanged,
      profileFormChanged,
    } = this.props

    const buttonClasses = classnames('refi-app__section-header__button', {
      'refi-app__section-header__button--open': uiStatus,
    })

    const sectionClass = classnames('', {
      'refi-app__section--open': uiStatus,
    })

    const handleSectionToggle = async () => {
      const opening = !uiStatus

      // When the Payment step is showing (Back / Save and Send buttons
      // visible), a click should NOT collapse the section. Instead re-rate the
      // package (GetRate) and step back to the products list so all products
      // are shown again. A subsequent click then collapses the section.
      if (!opening && coverageDetailStep === 'Payment') {
        try {
          await refreshCoverageProducts()
        } catch (error) {
          // GetRate failed — existing package.products remain as fallback
        }
        coverageDetailsPreviousStep()
        return
      }

      if (opening) {
        if (vehicleUiStatus && vehicleFormChanged) {
          submitFormByName('PlateVinForm', 'coverage')
        }

        if (profileUiStatus && profileFormChanged) {
          submitFormByName('CreateUserForm', 'coverage')
        }
      }

      toggleCoverageUiState()
    }

    return (
      <div className={sectionClass}>
        {user.id && creditSectionFinished ? (
          <div
            className="refi-app__section-header"
            onClick={handleSectionToggle}
            data-testid="refiCoverageToggle"
          >
            <h2>Coverage details</h2>
            <button className={buttonClasses}>Toggle</button>
            <hr className="form__hr" />
          </div>
        ) : null}

        {user.id && (
          <LoadingSection isLoading={isCreatingPackage}>
            {uiStatus && (
              <div className="form">
                <div className="refi-app__coverage-detail-form-fields">
                  <div className="coverage__header">
                    These products are available:
                  </div>

                  {coverageDetailStep === 'Membership' && <MembershipSection />}

                  {coverageDetailStep === 'CoverageProtection' && (
                    <CoverageProtectionSection />
                  )}

                  {coverageDetailStep === 'Payment' && <PaymentOptions />}
                </div>
              </div>
            )}
          </LoadingSection>
        )}
      </div>
    )
  }
}

export const mapStateToProps = (state) => ({
  uiStatus: selectRefiAppCoverageUiState(state),
  vehicleUiStatus: selectRefiAppVehicleUiState(state),
  profileUiStatus: selectRefiAppProfileUiState(state),
  vehicleFormChanged: hasFormChanges(state, 'plateVinForm'),
  profileFormChanged: hasFormChanges(state, 'ProfileDetailForm'),
  user: selectCurrentRefiUser(state),
  coverageDetailStep: selectCoverageStepState(state),
  creditSectionFinished: selectCreditSessionFinished(state),
  isCreatingPackage: selectProductPackageLoading(state),
})

export default connect(mapStateToProps, {
  toggleCoverageUiState,
  coverageDetailsPreviousStep,
  refreshCoverageProducts,
})(CoverageDetailsSections)
